import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { TraderLayout } from '../../layouts/TraderLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { StatusBadge } from '../../components/LiveIndicator';
import { 
  Trophy,
  Loader2,
  AlertCircle,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export function TraderDashboard() {
  const [races, setRaces] = useState([]);
  const [runners, setRunners] = useState({});
  const [exposure, setExposure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRaceId, setSelectedRaceId] = useState('');
  const [selectedBet, setSelectedBet] = useState(null);
  const [stake, setStake] = useState('');
  const [betType, setBetType] = useState('win');
  const [placing, setPlacing] = useState(false);
  const [hideEw, setHideEw] = useState(false);
  const [hidePlace, setHidePlace] = useState(false);
  const { user, refreshUser } = useAuth();
  const { subscribeToType } = useWebSocket();

  const fetchRaces = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/races`);
      // Show all races (open, suspended, settled) so trader can view their bets
      const allRaces = response.data;
      setRaces(allRaces);
      if (allRaces.length > 0 && !selectedRaceId) {
        // Default to first open race, or first race if none open
        const firstOpen = allRaces.find(r => r.status === 'open');
        setSelectedRaceId(firstOpen ? firstOpen.id : allRaces[0].id);
      }
    } catch (error) {
      toast.error('Failed to load races');
    } finally {
      setLoading(false);
    }
  }, [selectedRaceId]);

  const fetchRunners = useCallback(async (raceId) => {
    try {
      const response = await axios.get(`${API}/runners?race_id=${raceId}`);
      setRunners(prev => ({ ...prev, [raceId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch runners:', error);
    }
  }, []);

  const fetchExposure = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/exposure/user/${user.id}`);
      setExposure(response.data);
    } catch (error) {
      console.error('Failed to fetch exposure:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchRaces();
    fetchExposure();
    fetchSettings();
  }, [fetchRaces, fetchExposure]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/system/settings`);
      setHideEw(response.data.hide_ew === true);
      setHidePlace(response.data.hide_place === true);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  useEffect(() => {
    if (selectedRaceId) {
      fetchRunners(selectedRaceId);
    }
  }, [selectedRaceId, fetchRunners]);

  useEffect(() => {
    const unsubscribe = subscribeToType('*', (data) => {
      if (data.type === 'odds_updated' && selectedRaceId) {
        fetchRunners(selectedRaceId);
      } else if (data.type === 'race_status_changed' || data.type === 'race_created') {
        fetchRaces();
      } else if (data.type === 'bet_placed' && data.user_id === user?.id) {
        refreshUser();
        fetchExposure();
      } else if (data.type === 'bet_cancelled' && data.user_id === user?.id) {
        refreshUser();
        fetchExposure();
      } else if (data.type === 'race_settled') {
        fetchRaces();
        refreshUser();
        fetchExposure();
        toast.info(`Race settled: #${data.winning_runner_number} ${data.winning_runner_name} won!`);
      }
    });

    return unsubscribe;
  }, [subscribeToType, selectedRaceId, fetchRunners, fetchRaces, refreshUser, fetchExposure, user]);

  const selectedRace = races.find(r => r.id === selectedRaceId);

  const handleSelectRunner = (runner, type) => {
    if (!selectedRace || selectedRace.status !== 'open') {
      toast.error('Market is not open for betting');
      return;
    }
    if (type === 'place' && hidePlace) {
      toast.error('Place betting is disabled');
      return;
    }
    if (type === 'ew' && hideEw) {
      toast.error('Each-way betting is disabled');
      return;
    }
    setSelectedBet({ runner, race: selectedRace });
    setBetType(type);
    setStake('');
  };

  const handlePlaceBet = async () => {
    if (!selectedBet || !stake || parseFloat(stake) <= 0) {
      toast.error('Please enter a valid stake');
      return;
    }

    setPlacing(true);
    try {
      await axios.post(`${API}/bets`, {
        runner_id: selectedBet.runner.id,
        stake: parseFloat(stake),
        bet_type: betType
      });
      
      const betTypeLabel = betType === 'ew' ? 'EACH-WAY' : betType.toUpperCase();
      toast.success(`${betTypeLabel} bet placed on #${selectedBet.runner.number} ${selectedBet.runner.name}!`);
      setSelectedBet(null);
      setStake('');
      refreshUser();
      fetchExposure();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bet');
    } finally {
      setPlacing(false);
    }
  };

  // Calculate potential payout
  const calculatePayout = () => {
    if (!selectedBet || !stake) return { win: '0.00', place: '0.00', total: '0.00' };
    const stakeNum = parseFloat(stake);
    if (isNaN(stakeNum)) return { win: '0.00', place: '0.00', total: '0.00' };
    
    const { runner, race } = selectedBet;
    const winOdds = runner.odds_decimal;
    
    // Place odds calculation
    const [placeNum, placeDen] = race.place_terms.split('/').map(Number);
    const placeMultiplier = placeNum / placeDen;
    const placeOdds = ((runner.odds_numerator / runner.odds_denominator) * placeMultiplier) + 1;
    
    if (betType === 'win') {
      return { win: (stakeNum * winOdds).toFixed(2), place: '0.00', total: (stakeNum * winOdds).toFixed(2) };
    } else if (betType === 'place') {
      return { win: '0.00', place: (stakeNum * placeOdds).toFixed(2), total: (stakeNum * placeOdds).toFixed(2) };
    } else {
      // Each-way: half stake on win, half on place
      const halfStake = stakeNum / 2;
      const winReturn = halfStake * winOdds;
      const placeReturn = halfStake * placeOdds;
      return { 
        win: winReturn.toFixed(2), 
        place: placeReturn.toFixed(2), 
        total: (winReturn + placeReturn).toFixed(2) 
      };
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const payout = calculatePayout();
  
  // Check if user has show_exposure enabled
  const showExposure = user?.show_exposure !== false;

  const showPlaceCol = !hidePlace;
  const showEwCol = !hideEw;
  const runnerGridCols =
    showPlaceCol && showEwCol
      ? 'grid-cols-[36px_1fr_60px_60px_60px]'
      : showPlaceCol || showEwCol
        ? 'grid-cols-[36px_1fr_60px_60px]'
        : 'grid-cols-[36px_1fr_60px]';

  return (
    <TraderLayout>
      <div className="space-y-3 pb-20" data-testid="trader-dashboard">
        {/* Compact Exposure Summary - Only show if enabled */}
        {showExposure && (
          <div className="grid grid-cols-4 gap-1.5">
            <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500">Balance</p>
              <p className="font-mono text-sm font-bold text-zinc-100" data-testid="trader-balance">
                €{user?.balance?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500">Exposure</p>
              <p className="font-mono text-sm font-bold text-red-400" data-testid="trader-exposure">
                €{exposure?.total_exposure?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500">Limit</p>
              <p className="font-mono text-sm font-bold text-zinc-100">
                €{user?.liability_limit?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500">Free</p>
              <p className="font-mono text-sm font-bold text-emerald-400" data-testid="available-liability">
                €{exposure?.available_liability?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        )}

        {/* Race Selector Dropdown */}
        {loading ? (
          <div className="h-24 bg-zinc-900 animate-pulse rounded border border-zinc-800" />
        ) : races.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-8 text-center text-zinc-500">
              <Trophy className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No open markets available</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Race Dropdown */}
            <Select value={selectedRaceId} onValueChange={setSelectedRaceId}>
              <SelectTrigger 
                className="w-full h-14 bg-zinc-900 border-zinc-800 text-zinc-100"
                data-testid="race-selector"
              >
                <SelectValue placeholder="Select a race">
                  {selectedRace && (
                    <div className="flex items-center justify-between w-full pr-2">
                      <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-blue-500" />
                        <div className="text-left">
                          <span className="font-heading text-base font-bold">{selectedRace.venue}</span>
                          <span className="font-heading text-base font-bold text-blue-400 ml-2">{selectedRace.time}</span>
                        </div>
                      </div>
                      <span className="text-xs text-zinc-500">{formatDate(selectedRace.date)}</span>
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {races.map(race => (
                  <SelectItem key={race.id} value={race.id} className="py-3">
                    <div className="flex items-center gap-3">
                      <Trophy className="w-4 h-4 text-blue-500" />
                      <span className="font-bold">{race.venue}</span>
                      <span className="font-bold text-blue-400">{race.time}</span>
                      <span className="text-xs text-zinc-500 ml-auto">{formatDate(race.date)}</span>
                      {race.status !== 'open' && (
                        <StatusBadge status={race.status} />
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Race Info Bar */}
            {selectedRace && (
              <div className="flex items-center justify-between px-3 py-2 bg-zinc-800/50 rounded border border-zinc-800">
                <div className="flex items-center gap-2">
                  <StatusBadge status={selectedRace.status} />
                  <span className="text-xs text-zinc-400">
                    {selectedRace.num_runners} runners
                  </span>
                  <span className="text-xs font-medium text-blue-400">
                    {(selectedRace.num_places || 1)} place{(selectedRace.num_places || 1) !== 1 ? 's' : ''}
                  </span>
                  <span className="text-xs text-zinc-400">
                    {selectedRace.place_terms}
                  </span>
                </div>
              </div>
            )}

            {/* Runners Grid - Mobile Optimized */}
            {selectedRace && (
              <div className="space-y-1.5">
                {/* Header */}
                <div className={`grid ${runnerGridCols} gap-1 px-2 py-1.5 text-[10px] text-zinc-500 uppercase tracking-wider`}>
                  <span>#</span>
                  <span>Runner</span>
                  <span className="text-center">Win</span>
                  {showPlaceCol && <span className="text-center">Place</span>}
                  {showEwCol && <span className="text-center">E/W</span>}
                </div>
                {/* Runners */}
                {runners[selectedRaceId]?.map(runner => (
                  <div 
                    key={runner.id} 
                    className={`grid ${runnerGridCols} gap-1 items-center py-1.5 px-2 bg-zinc-900/50 rounded border border-zinc-800`}
                    data-testid={`runner-${runner.id}`}
                  >
                    <span className="font-mono text-sm font-bold text-zinc-400">{runner.number}</span>
                    <span className="text-sm text-zinc-100 truncate pr-1">{runner.name}</span>
                    <Button
                      size="sm"
                      className="h-10 px-1 bg-blue-600/20 hover:bg-blue-600/40 border border-blue-600/50 text-blue-400 font-mono text-xs font-bold"
                      onClick={() => handleSelectRunner(runner, 'win')}
                      disabled={selectedRace.status !== 'open'}
                      data-testid={`win-btn-${runner.id}`}
                    >
                      {runner.odds_numerator}/{runner.odds_denominator}
                    </Button>
                    {showPlaceCol && (
                      <Button
                        size="sm"
                        className="h-10 px-1 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-600/50 text-emerald-400 font-mono text-xs font-bold"
                        onClick={() => handleSelectRunner(runner, 'place')}
                        disabled={selectedRace.status !== 'open'}
                        data-testid={`place-btn-${runner.id}`}
                      >
                        {selectedRace.place_terms}
                      </Button>
                    )}
                    {showEwCol && (
                      <Button
                        size="sm"
                        className="h-10 px-1 bg-yellow-600/20 hover:bg-yellow-600/40 border border-yellow-600/50 text-yellow-400 font-mono text-[10px] font-bold"
                        onClick={() => handleSelectRunner(runner, 'ew')}
                        disabled={selectedRace.status !== 'open'}
                        data-testid={`ew-btn-${runner.id}`}
                      >
                        E/W
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Bet Slip Dialog - Mobile Optimized */}
        <Dialog open={!!selectedBet} onOpenChange={(open) => !open && setSelectedBet(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md mx-auto">
            <DialogHeader>
              <DialogTitle className="text-zinc-100">
                {betType === 'ew' ? 'Each-Way Bet' : betType === 'win' ? 'Win Bet' : 'Place Bet'}
              </DialogTitle>
            </DialogHeader>
            {selectedBet && (
              <div className="space-y-4 py-2">
                {/* Selection Info */}
                <div className="p-3 bg-zinc-800/50 rounded border border-zinc-800">
                  <div className="flex items-center gap-2 text-sm text-zinc-400 mb-1">
                    <span className="font-bold text-zinc-100">{selectedBet.race.venue}</span>
                    <span className="font-bold text-blue-400">{selectedBet.race.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg font-bold text-zinc-400">#{selectedBet.runner.number}</span>
                    <span className="text-base font-medium text-zinc-100">{selectedBet.runner.name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      betType === 'win' ? 'bg-blue-500/20 text-blue-400' : 
                      betType === 'place' ? 'bg-emerald-500/20 text-emerald-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {betType === 'ew' ? 'EACH-WAY' : betType.toUpperCase()}
                    </span>
                    <span className="font-mono text-lg font-bold text-zinc-100">
                      @{selectedBet.runner.odds_numerator}/{selectedBet.runner.odds_denominator}
                    </span>
                    {betType !== 'win' && (
                      <span className="text-xs text-zinc-500">({selectedBet.race.place_terms})</span>
                    )}
                  </div>
                </div>

                {/* Stake Input */}
                <div className="space-y-2">
                  <label className="text-sm text-zinc-300">
                    {betType === 'ew' ? 'Total Stake (€) - split equally' : 'Stake (€)'}
                  </label>
                  <Input
                    type="number"
                    step="0.50"
                    min="0"
                    inputMode="decimal"
                    placeholder="Enter stake..."
                    value={stake}
                    onChange={(e) => setStake(e.target.value)}
                    className="bg-black border-zinc-800 text-zinc-100 font-mono text-lg h-12"
                    data-testid="stake-input"
                  />
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Available: €{user?.balance?.toFixed(2)}</span>
                    {betType === 'ew' && stake && (
                      <span className="text-zinc-400">€{(parseFloat(stake)/2).toFixed(2)} each way</span>
                    )}
                  </div>
                </div>

                {/* Payout Display */}
                {stake && parseFloat(stake) > 0 && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded space-y-2">
                    {betType === 'ew' ? (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">If wins:</span>
                          <span className="font-mono font-bold text-emerald-400">€{payout.total}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">If places only:</span>
                          <span className="font-mono font-bold text-zinc-300">€{payout.place}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-sm text-zinc-400">Potential Return</span>
                        <span className="font-mono text-lg font-bold text-emerald-400" data-testid="potential-payout">
                          €{payout.total}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {parseFloat(stake) > user?.balance && (
                  <div className="flex items-center gap-2 text-sm text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    Insufficient balance
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <DialogClose asChild>
                <Button variant="outline" className="border-zinc-700 text-zinc-400 w-full sm:w-auto">Cancel</Button>
              </DialogClose>
              <Button 
                onClick={handlePlaceBet}
                disabled={placing || !stake || parseFloat(stake) <= 0 || parseFloat(stake) > user?.balance}
                className={`w-full sm:w-auto ${
                  betType === 'win' ? 'bg-blue-600 hover:bg-blue-700' : 
                  betType === 'place' ? 'bg-emerald-600 hover:bg-emerald-700' :
                  'bg-yellow-600 hover:bg-yellow-700'
                }`}
                data-testid="place-bet-btn"
              >
                {placing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Placing...
                  </>
                ) : (
                  `Place ${betType === 'ew' ? 'E/W' : betType.toUpperCase()} Bet`
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TraderLayout>
  );
}
